import React from 'react';
import { Grid, Card, Typography } from 'antd';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend
} from 'recharts';
import { formatDate } from '../../utils/dateFormatters';
import { filterByNits } from '../../utils/inventoryReports';

const { useBreakpoint } = Grid;
const { Text } = Typography;

const SERIES_CONFIG = [
  { key: 'negro',    name: 'Negro (<10%)',       color: '#333333' },
  { key: 'rojo',     name: 'Rojo (10-30%)',      color: '#FF0000' },
  { key: 'amarillo', name: 'Amarillo (30-70%)',  color: '#ca8a04' },
  { key: 'verde',    name: 'Verde (70-100%)',     color: '#16a34a' },
  { key: 'azul',     name: 'Azul (>100%)',        color: '#2563eb' },
];

const REVERSED_SERIES = [...SERIES_CONFIG].reverse();

/**
 * HealthEvolutionChart - Stacked 100% area chart showing inventory buffer levels
 *
 * Displays the proportion of active references in each inventory level category:
 * - Negro (≤10%): very low
 * - Rojo (10-30%): low
 * - Amarillo (30-60%): medium
 * - Verde (60-80%): good
 * - Azul (>80%): overstock
 *
 * @param {Object} props
 * @param {Array} props.data - Array of DailyInventoryDistributionRecord
 * @param {string[]} props.nitList - Selected NITs for filtering
 * @param {boolean} props.groupByNit - Whether data is grouped by NIT
 */
function HealthEvolutionChart({ data, nitList, groupByNit }) {
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  // Transform data into normalized 0-1 proportions, sorted by date
  const chartData = React.useMemo(() => {
    if (!data || data.length === 0) return [];

    const relevantData = filterByNits(data, nitList);

    const groupedByDate = {};
    relevantData.forEach(record => {
      const date = record.RecordDate;
      if (!groupedByDate[date]) {
        groupedByDate[date] = {
          RecordDate: date,
          NegroCount: 0,
          RojoCount: 0,
          AmarilloCount: 0,
          VerdeCount: 0,
          AzulCount: 0,
          TotalReferences: 0,
        };
      }
      groupedByDate[date].NegroCount   += record.NegroCount   || 0;
      groupedByDate[date].RojoCount    += record.RojoCount    || 0;
      groupedByDate[date].AmarilloCount += record.AmarilloCount || 0;
      groupedByDate[date].VerdeCount   += record.VerdeCount   || 0;
      groupedByDate[date].AzulCount    += record.AzulCount    || 0;
      groupedByDate[date].TotalReferences += record.TotalReferences || 0;
    });

    return Object.values(groupedByDate)
      .sort((a, b) => a.RecordDate.localeCompare(b.RecordDate))
      .map(d => {
        const total = d.TotalReferences || 1;
        return {
          RecordDate: d.RecordDate,
          negro:    d.NegroCount   / total,
          rojo:     d.RojoCount    / total,
          amarillo: d.AmarilloCount / total,
          verde:    d.VerdeCount   / total,
          azul:     d.AzulCount    / total,
          // Keep raw counts for tooltip detail
          NegroCount:    d.NegroCount,
          RojoCount:     d.RojoCount,
          AmarilloCount: d.AmarilloCount,
          VerdeCount:    d.VerdeCount,
          AzulCount:     d.AzulCount,
          TotalReferences: d.TotalReferences,
        };
      });
  }, [data, nitList]);

  // Calculate tick interval: show ~15 labels max
  const tickInterval = React.useMemo(() => {
    if (chartData.length <= 15) return 0;
    return Math.floor(chartData.length / 15);
  }, [chartData.length]);

  // Custom tooltip – reversed order (azul first, negro last)
  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;

    const entry = chartData.find(d => d.RecordDate === label);
    if (!entry) return null;

    return (
      <div style={{
        backgroundColor: 'white',
        border: '1px solid #d1d5db',
        borderRadius: '8px',
        padding: '12px',
        fontSize: '12px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        minWidth: '200px',
      }}>
        <p style={{ margin: '0 0 8px 0', fontWeight: 600, color: '#1f2937' }}>
          {formatDate(label)}
        </p>
        {REVERSED_SERIES.map(({ key, name, color }) => (
          <p key={key} style={{ margin: '3px 0', color, fontWeight: 500 }}>
            {name}: {(entry[key] * 100).toFixed(1)}%
          </p>
        ))}
        <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid #e5e7eb' }}>
          <p style={{ margin: 0, fontWeight: 600, color: '#6b7280', fontSize: '11px' }}>
            Total: {entry.TotalReferences} SKUs
          </p>
        </div>
      </div>
    );
  };

  if (!chartData || chartData.length === 0) {
    return (
      <Card
        bordered={false}
        className="shadow-sm"
        style={{ height: '100%', borderRadius: '12px', backgroundColor: '#fafafa' }}
      >
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 300,
          color: '#9ca3af',
          textAlign: 'center',
          padding: 20,
        }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>{'💚'}</div>
          <div style={{ fontSize: isMobile ? 14 : 16, fontWeight: 500, marginBottom: 8 }}>
            Datos insuficientes
          </div>
          <div style={{ fontSize: isMobile ? 12 : 13 }}>
            Se requieren datos de inventario para mostrar la evolución de salud
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card
      bordered={false}
      className="shadow-sm"
      style={{
        height: '100%',
        borderRadius: '12px',
        boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
      }}
      bodyStyle={{ padding: isMobile ? 16 : 20 }}
    >
      {/* Header */}
      <div style={{ marginBottom: isMobile ? 16 : 20 }}>
        <Text strong style={{ fontSize: isMobile ? 14 : 16, color: '#1f2937' }}>
          {'Evolución de la Salud del Inventario'}
        </Text>
        <br />
        <Text type="secondary" style={{ fontSize: isMobile ? 11 : 12 }}>
          Proporción de productos en cada nivel de salud a lo largo del tiempo. El area verde/azul mas grande indica mejor salud general.
        </Text>
      </div>

      {/* Chart */}
      <ResponsiveContainer width="100%" height={450}>
        <AreaChart
          data={chartData}
          margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis
            dataKey="RecordDate"
            tickFormatter={formatDate}
            angle={-45}
            textAnchor="end"
            height={80}
            style={{ fontSize: isMobile ? 9 : 11, color: '#6b7280' }}
            tickLine={false}
            interval={tickInterval}
          />
          <YAxis
            domain={[0, 1]}
            style={{ fontSize: isMobile ? 9 : 11, color: '#6b7280' }}
            tickFormatter={(v) => `${(v * 100).toFixed(0)}%`}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend
            wrapperStyle={{ fontSize: isMobile ? 10 : 12, paddingTop: 8 }}
            iconType="circle"
          />
          {SERIES_CONFIG.map(({ key, name, color }) => (
            <Area
              key={key}
              type="monotone"
              dataKey={key}
              name={name}
              stackId="1"
              stroke="none"
              fill={color}
              fillOpacity={1}
            />
          ))}
        </AreaChart>
      </ResponsiveContainer>
    </Card>
  );
}

export default HealthEvolutionChart;
